import './BuyersFeesTable.scss';
import React from 'react';
import { FormGroup, Col, Row } from 'reactstrap';
import { Input } from '@availity/form';
import InfoTooltip from '../../../../../components/info-tooltip/info-tooltip';
import { MarketplaceCurrencyField } from '../MarketplaceSettingsFields/MarketplaceCurrencyField';
import {
    FeeRefundStatus,
    FeeRefundStatuses,
} from '../../../../../slices/marketplace/pricing/models/fee-refund-status.enum';
import { BuyersFees } from '../../../../../slices/marketplace/pricing/models/buyers-fees-model';
import { MarketplacePricing } from '../../../../../slices/marketplace/pricing/models/marketplace-pricing.model';

interface BuyersFeesTableProps {
    buyersFees: BuyersFees;
    onChange: (newState: BuyersFees) => void;
    setFormFieldValue: (name: string, value: string) => void;
    formState: MarketplacePricing;
}

const BuyersFeesTable = (props: BuyersFeesTableProps) => {
    const labelFieldWidthMd = 2;
    const multiFieldWidthMd = 5;
    const refundableFieldWidthMd = 2;
    const creditableFieldWidthMd = 2;

    const partiallyRefundableTooltipText =
        'The application fee will be partially refunded in each of the following circumstances: ' +
        '1. 50% refund if a completed process yeilds no LOIs, ' +
        '2. 25% refund if a completed process yeilds ONLY 1 LOI, ' +
        "3. Up to 50% refund, at VetValue's discretion, depending upon the circumstances, if the engagement application is denied by VetValue";

    const priceField = (fieldId: keyof BuyersFees) => (
        <>
            <div className="shorter-field">
                <MarketplaceCurrencyField
                    name={`buyersFees.${fieldId}`}
                    value={props.formState.buyersFees[fieldId]}
                    setFormValue={(name: string, value: string): void => {
                        props.setFormFieldValue(name, value);
                        const newState = {
                            ...props.buyersFees,
                        };
                        const numVal = Number(value);
                        newState[fieldId] = numVal;
                        props.onChange({
                            ...newState,
                        });
                    }}
                />
            </div>
        </>
    );

    const refundableField = (fieldId: keyof BuyersFees) => (
        <>
            <div
                className={`shorter-field ${
                    props.buyersFees[fieldId] == FeeRefundStatus.Partially
                        ? ''
                        : 'info-tooltip-offset'
                }`}>
                <Input
                    type="select"
                    name={`buyersFees.${fieldId}`}
                    value={props.formState.buyersFees[fieldId]}
                    onChange={(event) => {
                        const numVal = Number(event.currentTarget.value);
                        const newState = {
                            ...props.buyersFees,
                        };
                        newState[fieldId] = numVal;
                        props.onChange({ ...newState });
                    }}>
                    {FeeRefundStatuses.map((item) => (
                        <option value={item.id} key={fieldId + item.id}>
                            {item.name}
                        </option>
                    ))}{' '}
                </Input>
            </div>
            {props.buyersFees[fieldId] == FeeRefundStatus.Partially ? (
                <InfoTooltip
                    idText={`buyersFees.${fieldId}`}
                    text={partiallyRefundableTooltipText}
                />
            ) : (
                []
            )}
        </>
    );

    return (
        <div className="buyers-fees-table">
            <FormGroup>
                <Row className="center-items fee-item-row margin-bottom-1-rem">
                    <Col md={labelFieldWidthMd}>
                        <label>Fee Type</label>
                    </Col>
                    <Col
                        md={multiFieldWidthMd}
                        className="conditional-text-center">
                        <label>Fee Amount</label>
                    </Col>
                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center aligned-md-2">
                        <label className="refundable-label">Refundable</label>
                    </Col>
                    <Col
                        md={creditableFieldWidthMd}
                        className="conditional-text-center aligned-md-2">
                        <label>Creditable</label>
                        <InfoTooltip
                            idText={`buyers-fees-creditable-header`}
                            text={'Creditable against the transaction fee'}
                        />
                    </Col>
                </Row>

                <Row className="center-items fee-item-row margin-bottom-1-rem">
                    <Col md={labelFieldWidthMd}>
                        <div className="label-with-tooltip">
                            <label>Seller Identification Fee</label>
                        </div>
                    </Col>
                    <Col
                        md={multiFieldWidthMd}
                        className="right-padding-5 multi-field">
                        {priceField('sellerIdFeePerDvm')}
                        <label className="label-right">* FTE DVMs</label>
                    </Col>
                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                        {refundableField('sellerIdFeeRefundable')}
                    </Col>
                    <Col
                        md={creditableFieldWidthMd}
                        className={`conditional-text-center aligned-md-2`}>
                        <label>NA</label>
                    </Col>
                </Row>

                <Row className="center-items fee-item-row margin-bottom-1-rem">
                    <Col md={labelFieldWidthMd}>
                        <div className="label-with-tooltip">
                            <label>PIP/PIQ Purchase Fee</label>
                        </div>
                    </Col>
                    <Col
                        md={multiFieldWidthMd}
                        className="right-padding-5 multi-field">
                        {priceField('pipAccessFeePerDvm')}
                        <label className="label-right">* FTE DVMs</label>
                    </Col>
                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                        {refundableField('pipAccessFeeRefundable')}
                    </Col>
                    <Col
                        md={creditableFieldWidthMd}
                        className={`conditional-text-center aligned-md-2`}>
                        <label>NA</label>
                    </Col>
                </Row>

                <Row className="center-items fee-item-row margin-bottom-1-rem">
                    <Col md={labelFieldWidthMd}>
                        <div className="label-with-tooltip">
                            <label>DFM Purchase Fee</label>
                        </div>
                    </Col>
                    <Col
                        md={multiFieldWidthMd}
                        className="right-padding-5 multi-field">
                        {priceField('dfmAccessFeePerDvm')}
                        <label className="label-right">* FTE DVMs</label>
                    </Col>
                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                        {refundableField('dfmAccessFeeRefundable')}
                    </Col>
                    <Col
                        md={creditableFieldWidthMd}
                        className={`conditional-text-center aligned-md-2`}>
                        <label>NA</label>
                    </Col>
                </Row>

                <Row className="center-items fee-item-row margin-bottom-1-rem">
                    <Col md={labelFieldWidthMd}>
                        <div className="label-with-tooltip">
                            <label>Seller Meeting Fee</label>
                        </div>
                    </Col>

                    <Col md={multiFieldWidthMd} className="right-padding-5">
                        <Col className="multi-field left-padding-0 margin-bottom-half-rem">
                            {priceField('sellerMeetingFeeVirtual')}
                            <label className="label-right">
                                per virtual meeting
                            </label>
                        </Col>

                        <Col className="multi-field left-padding-0">
                            {priceField('sellerMeetingFeeInPerson')}
                            <label className="label-right">
                                per in-person meeting
                            </label>
                        </Col>
                    </Col>

                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                        {refundableField('sellerMeetingFeeRefundable')}
                    </Col>
                    <Col
                        md={creditableFieldWidthMd}
                        className={`conditional-text-center aligned-md-2`}>
                        <label>NA</label>
                    </Col>
                </Row>
            </FormGroup>
        </div>
    );
};

export default BuyersFeesTable;
